/**
 * A chore category, as an open string rather than a closed union.
 *
 * It was seven fixed values (`'quotidien' | 'sols' | …`) and is rendered
 * nowhere. A fixed set cannot survive chores a household defines for itself —
 * `docs/architecture-review.md` §1.3 — and widening it now costs nothing,
 * because nothing branches on the value. Narrow it again only if something ever
 * does.
 */
export type TaskCategory = string

export type TaskTemplate = {
  /**
   * Permanent. It is embedded in the primary key of every completion ever
   * recorded against this chore, locally and remotely, so renaming one orphans
   * every past checkmark — `CLAUDE.md` §4. `label`, `emoji` and `category` are
   * editorial and free to change.
   */
  id: string
  label: string
  emoji: string
  category: TaskCategory
  /**
   * Whose chore it is: a `household_members.id`, or absent for the whole house.
   *
   * Optional so that the built-in rotation in `src/data/default-plan.ts` stays
   * the literal it is — a plan nobody assigned is a plan everybody shares, and
   * that is what every chore in this app was until members existed.
   *
   * Assignment is a *filter*, not a scope: an unassigned chore gives each member
   * their own completion row on the same day, which is exactly what the member
   * component of the completion id was always for.
   */
  assigneeId?: string
}

/**
 * Where in the plan a household-defined chore belongs.
 *
 * The five slots are exactly the ones `tasksForDate` reads, plus `once`, which
 * is the only one the built-in plan has no room for: "sortir les poubelles
 * jeudi prochain" is a real thing to want and it is not a rotation.
 *
 * The array is the source of truth and the type is derived from it, so the
 * value a wire row is checked against cannot drift from the union the compiler
 * enforces.
 */
export const SCHEDULE_KINDS = [
  'daily',
  'weekday',
  'saturday-rotation',
  'monthly-sunday',
  'once',
] as const

export type ScheduleKind = (typeof SCHEDULE_KINDS)[number]

export function isScheduleKind(value: unknown): value is ScheduleKind {
  return typeof value === 'string' && (SCHEDULE_KINDS as readonly string[]).includes(value)
}

/**
 * A chore the household added itself, as the plan builder wants it.
 *
 * `weekday`, `cycleWeek` and `onDate` are always filled in — they record the
 * day the chore was created from — but only the one its `schedule` names is
 * read. Storing all three costs three columns and means changing a chore's
 * recurrence later never has to invent a value it never had.
 *
 * `src/store/types.ts` adds the sync envelope to this and nothing else.
 */
export type CustomChore = TaskTemplate & {
  /**
   * Required here, unlike on `TaskTemplate`: this is a row, and a row with an
   * absent column reads back differently on the two devices that wrote it. `''`
   * is the value that means "toute la maison", and it is what the column
   * defaults to (`supabase/migrations/0006_members_and_invites.sql`).
   */
  assigneeId: string
  schedule: ScheduleKind
  /** `Date.getDay()`, 0–6. Read when `schedule` is `weekday`. */
  weekday: number
  /** 0–3. Read when `schedule` is `saturday-rotation`. */
  cycleWeek: number
  /** `YYYY-MM-DD`. Read when `schedule` is `once`. */
  onDate: string
}

/** A chore on its way to the store, before it has been given its permanent id. */
export type NewChore = Omit<CustomChore, 'id'>

/**
 * A household's chore plan: what to do, on which day, in which week of the
 * four-week cycle.
 *
 * It is a *parameter* of `tasksForDate` rather than an import inside it so that
 * where the chores come from is the caller's business and the rotation engine —
 * the part with logic that can be wrong — stays pure either way. That seam is
 * now load-bearing rather than hypothetical: `buildPlan` folds the household's
 * own chores into the literal in `src/data/default-plan.ts`, and neither the
 * engine nor any page can tell which chore came from where.
 *
 * The two rotating parts are tuples rather than records on purpose: the
 * compiler then refuses a plan that forgets a weekday or a cycle week, which is
 * the totality `CLAUDE.md` §4 asks for, enforced instead of tested.
 */
export type Plan = {
  /** Done every day, in every week of the cycle. */
  daily: readonly TaskTemplate[]
  /** Indexed by `Date.getDay()` — 0 is Sunday, as JavaScript has it. */
  weekly: WeekdayTable
  /** Added on Saturdays, indexed by the cycle week 0–3. */
  saturdayRotation: CycleTable
  /** Added on the Sunday of cycle week 3 — once every four weeks. */
  monthlySunday: readonly TaskTemplate[]
  /**
   * Added on one date only, keyed `YYYY-MM-DD`.
   *
   * A record rather than a tuple because the keys are dates and there is no
   * finite set of them to be total over. Required rather than optional so that
   * a future plan source cannot forget it and silently drop every one-off
   * chore — the failure would look exactly like a sync bug.
   */
  oneOff: Readonly<Record<string, readonly TaskTemplate[]>>
}

/** Seven entries, Sunday first. */
export type WeekdayTable = readonly [
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
]

/** Four entries, one per week of the cycle. */
export type CycleTable = readonly [
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
  readonly TaskTemplate[],
]
